'use client'

import { Check, ChevronDown } from 'lucide-react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'

import { LocaleFlag } from '@/components/atoms/locale-flag'
import { LOCALES, type LocaleCode, withLocale } from '@/lib/locales'
import { RADIUS, SURFACE_SOLID } from '@/lib/surface'
import { MICRO } from '@/lib/typography'
import { cn } from '@/lib/utils'

/**
 * Language switcher. Lives in the footer, and only there.
 *
 * The header is already carrying seven nav items, a basket and an account
 * button on one line; a language control there would be the first thing to
 * break on a phone and the last thing anyone came for. In the footer it sits
 * where a visitor goes looking for it.
 *
 * The menu opens UPWARD, because the trigger is at the bottom of the document
 * and a downward panel would open off-screen.
 */
export function LocaleDropdown({
  current,
  triggerClassName,
}: {
  current: LocaleCode
  /** Replaces the default pill look — the footer draws its own button. */
  triggerClassName?: string
}) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const itemRefs = useRef<(HTMLAnchorElement | null)[]>([])
  const pathname = usePathname()

  const options = LOCALES.filter((locale) => locale.available)

  useEffect(() => {
    if (!open) return

    const onPointer = (event: PointerEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      // Focus goes back to the trigger, not to wherever the document left it —
      // a keyboard user who escapes a menu expects to land where they opened it.
      triggerRef.current?.focus()
    }

    document.addEventListener('pointerdown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // A `role="menu"` is arrow-navigated rather than tab-navigated, so opening it
  // has to place focus inside — on the language already in use.
  useEffect(() => {
    if (!open) return
    const index = Math.max(
      0,
      options.findIndex((locale) => locale.code === current),
    )
    const frame = requestAnimationFrame(() => itemRefs.current[index]?.focus())
    return () => cancelAnimationFrame(frame)
  }, [open, current, options])

  const focusItem = (index: number) => {
    const count = options.length
    itemRefs.current[((index % count) + count) % count]?.focus()
  }

  const onMenuKeyDown = (event: React.KeyboardEvent) => {
    const active = itemRefs.current.findIndex((element) => element === document.activeElement)
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        focusItem(active + 1)
        break
      case 'ArrowUp':
        event.preventDefault()
        focusItem(active - 1)
        break
      case 'Tab':
        setOpen(false)
        break
      default:
        break
    }
  }

  return (
    <div ref={wrapRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        // The CODE is in the name on purpose. WCAG 2.5.3 asks that an
        // accessible name contain the label a person can SEE, so that "kliknij
        // pe-el" reaches the same control a mouse does — and all this button
        // shows is `pl`. "Język: Polski" does not contain it.
        aria-label={`Język: ${current} — ${LOCALES.find((locale) => locale.code === current)?.name ?? current}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
        onKeyDown={(event) => {
          if (event.key !== 'ArrowDown') return
          event.preventDefault()
          setOpen(true)
        }}
        className={
          triggerClassName ??
          cn(
            'inline-flex min-h-11 items-center gap-2 border border-foreground/15 px-3 text-foreground/70',
            'transition-colors hover:border-foreground/30 hover:text-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/50',
            RADIUS.pill,
          )
        }
      >
        <LocaleFlag locale={current} className="h-3.5 w-5" />
        <span
          className={
            triggerClassName ? 'uppercase' : cn(MICRO, 'font-semibold tracking-[0.12em] uppercase')
          }
        >
          {current}
        </span>
        <ChevronDown
          size={13}
          aria-hidden="true"
          className={cn('transition-transform duration-200', open && 'rotate-180')}
        />
      </button>

      {open ? (
        <div
          role="menu"
          aria-label="Język"
          onKeyDown={onMenuKeyDown}
          className={cn(
            'absolute right-0 bottom-full z-50 mb-2 min-w-48 p-1 shadow-[0_16px_48px_-16px_rgba(0,0,0,0.6)]',
            RADIUS.box,
            SURFACE_SOLID,
          )}
        >
          {options.map((locale, index) => {
            const active = locale.code === current
            return (
              <Link
                key={locale.code}
                ref={(element) => {
                  itemRefs.current[index] = element
                }}
                href={withLocale(pathname, locale.code)}
                hrefLang={locale.code}
                role="menuitem"
                tabIndex={-1}
                aria-current={active ? 'true' : undefined}
                onClick={() => setOpen(false)}
                className={cn(
                  'flex items-center justify-between gap-8 rounded-lg px-3 py-2.5 text-sm outline-none',
                  'transition-colors hover:bg-white/[0.07] focus-visible:bg-white/[0.07]',
                  active ? 'text-foreground' : 'text-foreground/70 hover:text-foreground',
                )}
              >
                <span className="flex items-center gap-2.5">
                  <LocaleFlag locale={locale.code} className="h-3.5 w-5" />
                  {locale.name}
                </span>
                {active ? <Check size={14} aria-hidden="true" /> : null}
              </Link>
            )
          })}

          {/* Languages that are registered but not shipped. Rendered rather than
              hidden so the control says what is coming, and inert rather than
              linked so it cannot walk anyone into a 404. `aria-disabled` rather
              than omitted: a menu that announces two items and offers one is
              the honest version of what the eye already sees. */}
          {LOCALES.filter((locale) => !locale.available).map((locale) => (
            <span
              key={locale.code}
              role="menuitem"
              aria-disabled="true"
              // Reachable programmatically but never in the tab order — the
              // menu is arrow-navigated, and the arrow handler walks the links
              // only, so a disabled row is announced but not stopped on.
              tabIndex={-1}
              className="flex items-center justify-between gap-8 rounded-lg px-3 py-2.5 text-sm text-foreground/35"
            >
              <span className="flex items-center gap-2.5">
                <LocaleFlag locale={locale.code} className="h-3.5 w-5 opacity-45" />
                {locale.name}
              </span>
              <span className={cn(MICRO, 'uppercase')}>wkrótce</span>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  )
}
